import { Chip } from '@mui/material';
import { CreatedBy } from 'components/common/CreatedBy/CreatedBy';
import { DataTable, DataTableProps } from 'components/data/DataTable/DataTable';
import { format } from 'date-fns';
import {
    formatDecimalToFixedNumber,
    formatDecimalToFixedPercent,
} from 'helpers';
import { maxValidFill } from 'helpers/planning';
import {
    convertDeliveryStatusToDisplayName,
    convertDeliveryStatusToStatusColor,
} from 'lib/constants';
import { useEffect, useState } from 'react';
import {
    useLazyGetDeliveryTicketsByDeviceIdQuery,
    useLazyGetResolvedDeliveryTicketsByDeviceIdQuery,
} from 'store/services/planning';
import { Device } from 'types';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { DeliveryDetailsModal } from 'views/Planning/modals/DeliveryDetailsModal/DeliveryDetailsModal';
import styles from './DeviceDeliveries.module.scss';

export interface DeviceDeliveryTicketsProps {
    device: Device;
}

export function DeviceDeliveries({ device }: DeviceDeliveryTicketsProps) {
    const deviceDeliveryTicketColumns: DataTableProps['columns'] = [
        {
            headerName: 'Status',
            field: 'status',
            renderCell: (params) => {
                const ticket = params?.row;
                return (
                    <Chip
                        sx={{
                            backgroundColor:
                                convertDeliveryStatusToStatusColor[
                                    ticket?.status
                                ],
                        }}
                        label={
                            convertDeliveryStatusToDisplayName[ticket?.status]
                        }
                    />
                );
            },
        },
        {
            headerName: 'Delivery Date',
            field: 'dateDelivered',
            valueGetter: (params) => {
                if (params.row.dateDelivered) {
                    return format(
                        new Date(params.row.dateDelivered),
                        'yyyy/MM/dd'
                    );
                }
            },
        },
        {
            headerName: 'Gallons Delivered',
            field: 'gallonsDelivered',
            valueGetter: (params) => {
                if (params.row.gallonsDelivered) {
                    return `${Number(params.row.gallonsDelivered)} gal`;
                }
            },
        },
        {
            headerName: 'Planned Delivery Date',
            field: 'plannedDeliveryDate',
            valueGetter: (params) => {
                return params.row.plannedDate || device?.duf_7
                    ? format(
                          new Date(params.row.plannedDate ?? device?.duf_7),
                          'yyyy/MM/dd'
                      )
                    : '';
            },
        },
        {
            headerName: 'Planned Delivery',
            field: 'plannedDeliveryAmount',
            valueGetter: (params) => {
                return `${Number(
                    params.row.plannedGallons || maxValidFill(device) || 0
                )} gal`;
            },
        },
        {
            headerName: 'Created By',
            field: 'createdBy',
            renderCell: (params) => {
                return <CreatedBy ticket={params.row} showIcon={false} />;
            },
        },
        {
            headerName: 'Tank %',
            field: 'device.lastReading.tank',
            fbKey: 'tank',
            valueFormatter: () => {
                return (
                    `${formatDecimalToFixedNumber(
                        device.lastReading?.tank
                    )}%` ?? ''
                );
            },
        },
        {
            headerName: 'Ullage',
            field: 'device.ullage',
            fbKey: 'ullage',
            valueFormatter: () => {
                return `${device.ullage?.toFixed(2)} gal` ?? '';
            },
        },
        {
            headerName: 'Last Delivery Date',
            field: 'lastFillup',
            fbKey: 'lastFillup',
            valueFormatter: (params) => {
                if (!params.value || !params.value.time) return '';
                return format(new Date(params.value.time), 'yyyy/MM/dd');
            },
        },
        {
            headerName: 'Last Delivery %',
            field: 'lastFillup.diff',
            valueFormatter: (params) => {
                return params.value
                    ? formatDecimalToFixedPercent(params.value)
                    : '';
            },
            fbKey: 'diff',
        },
        {
            headerName: 'Last Delivery',
            field: 'lastFillup.amount',
            fbKey: 'amount',
            valueFormatter: (params) => {
                return params.value ? `${params.value.toFixed(2)} gal` : '';
            },
        },
    ];

    const [tableData, setTableData] = useState<Record<string, any>[] | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [isDetailsModalOpen, setIsDetailsModalOpen] =
        useState<boolean>(false);
    const [activeTicket, setActiveTicketForModal] = useState<Ticket | null>(
        null
    );

    const supplierId = device.protected?.supplierId; // ?? device.virtual_supplier?.[0];

    const [getDeliveriesQuery] = useLazyGetDeliveryTicketsByDeviceIdQuery();
    const [getResolvedDeliveriesQuery] =
        useLazyGetResolvedDeliveryTicketsByDeviceIdQuery(); // TODO: find something to test this with, combine these two calls

    useEffect(() => {
        if (supplierId !== undefined) {
            Promise.all([
                getDeliveriesQuery({
                    supplierId,
                    deviceId: device.deviceId,
                }),
                getResolvedDeliveriesQuery({
                    supplierId,
                    deviceId: device.deviceId,
                }),
            ]).then(async (result) => {
                if (result) {
                    let ticketsList: Ticket[] = [];

                    if (result[0]?.data) {
                        ticketsList = Object.values(result[0]?.data);
                    }

                    if (result[1]?.data) {
                        ticketsList = [
                            ...ticketsList,
                            ...Object.values(result[1]?.data),
                        ];
                    }

                    if (!ticketsList.length) {
                        return setLoading(false);
                    }

                    ticketsList.filter(
                        (ticket) => ticket.ticketCategory === 'delivery'
                    );

                    await Promise.all(ticketsList).then((result) => {
                        setTableData(result);
                    });

                    setLoading(false);
                } else {
                    setLoading(false);
                }
            });
        } else {
            setLoading(false);
        }
    }, [
        device.deviceId,
        supplierId,
        getDeliveriesQuery,
        getResolvedDeliveriesQuery,
    ]);

    return (
        <div className={styles.container}>
            <DataTable
                id="DeviceDeliveryTickets"
                data={tableData}
                columns={deviceDeliveryTicketColumns}
                loading={loading}
                dataFormatter={(data) => data}
                csvOptions={{
                    fileName: `delivery-history-${device.shortDeviceId}-${device.customer_name}`,
                }}
                onRowClick={(params) => {
                    setIsDetailsModalOpen(true);
                    setActiveTicketForModal(params.row);
                }}
                getRowCount={(data) => data?.length ?? 0}
            />
            {activeTicket && (
                <DeliveryDetailsModal
                    modalOpen={isDetailsModalOpen}
                    setModalOpen={setIsDetailsModalOpen}
                    ticket={activeTicket}
                    setActiveTicket={setActiveTicketForModal}
                    device={device}
                />
            )}
        </div>
    );
}
